import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import {
  SubscriptionAddressModal,
  SubscriptionPaymentModal,
} from 'src/views/components/MultipleSubscriptionComponents'
import { PUBSUB } from 'src/pubsub/pubEventNames'
import { subscribe, unSubscribeEvent, publish } from 'src/pubsub/subscribe'
import {
  addressContainer,
  cartContainer,
  multipleSubscriptionContainer,
  checkoutContainer,
} from 'src/models'
import { toastState, overlayState } from 'src/views/components'
import {
  i18nTranslate,
  isFeatureEnabled,
  trackPincodeError,
  isGPSEnabled,
} from 'src/utils'
import { pageNames } from 'src/routes/pathParams'
import { AddressEditModal } from 'src/views/components/MultipleSubscriptionComponents/SubscriptionAddressModal/AddressEditModal'
import './styles.scss'

@observer
class SubscriptionModal extends React.Component {
  @observable isToShowAddressModal = false
  @observable selectedAddressId = ''
  @observable isToShowSavedAddressEditModal = false
  @observable selectedAddressDataToEdit = {}
  @observable isToShowPaymentModal = false
  @observable selectedPaymentId = ''
  shippingAddressModalToken = null
  subscriptionPaymentModalToken = null
  redirectToOverrideCartToken = null

  constructor(props) {
    super(props)
  }

  componentDidMount() {
    this.subscribeSubscriptionAddressEvent()
    this.subscribeSubscriptionPaymentEvent()
    this.subscribeRedirectToOverrideCartPageEvent()
  }

  subscribeRedirectToOverrideCartPageEvent = () => {
    this.redirectToOverrideCartToken = subscribe(
      PUBSUB.REDIRECT_TO_SUBSCRIPTION_OVERRIDE_CART_PAGE,
      this.redirectToOverrideCartPage
    )
  }

  redirectToOverrideCartPage = async (options = '', data = {}) => {
    const { publishData = {} } = data || {}
    const {
      subscriptionName = '',
      redirectToOverrideCart = false,
      subscriptionId = '',
      promoCode = '',
    } = publishData || {}
    if (redirectToOverrideCart) {
      overlayState.showLoader()
      cartContainer.selectedSubscriptionName = subscriptionName
      await multipleSubscriptionContainer.updateSubscriptionDataToCart(
        subscriptionName,
        subscriptionId,
        promoCode
      )
      if (promoCode) {
        await multipleSubscriptionContainer.applyPromoToOverrideCart(promoCode)
      }
      await cartContainer.viewCart(null, false, true)
      overlayState.hideLoader()
      this.props.history?.push(pageNames.subscriptionCart)
    }
  }

  subscribeSubscriptionAddressEvent = () => {
    this.shippingAddressModalToken = subscribe(
      PUBSUB.SHOW_SHIPPING_ADDRESS,
      this.showShippingAddressModal
    )
  }

  publishSavedAddressEvent = (savedAddress = {}) => {
    try {
      publish(PUBSUB.SAVE_SELECTED_ADDRESS, { addressData: savedAddress })
    } catch (error) {
      console.error('** SF - Error publishing event:', error)
    }
  }

  publishAddAddressEvent = (addedAddress = {}) => {
    try {
      publish(PUBSUB.ADD_NEW_ADDRESS, { addressData: addedAddress })
    } catch (error) {
      console.error('** SF - Error publishing:', error)
    }
  }

  subscribeSubscriptionPaymentEvent = () => {
    this.subscriptionPaymentModalToken = subscribe(
      PUBSUB.SHOW_SUBSCRIPTION_PAYMENT,
      this.showSubscriptionPaymentModal
    )
  }

  publishSavedPaymentEvent = (paymentData = {}) => {
    try {
      publish(PUBSUB.SAVED_SUBSCRIPTION_PAYMENT, { paymentData: paymentData })
    } catch (error) {
      console.error('** SF - Error publishing event:', error)
    }
  }

  publishAddNewPaymentEvent = (paymentData = {}) => {
    try {
      publish(PUBSUB.ADD_SUBSCRIPTION_PAYMENT, { paymentData: paymentData })
    } catch (error) {
      console.error('** SF - Error publishing:', error)
    }
  }

  publishTriggerAddressApiEvent = (
    isFrom = '',
    selectedAddressId = 0,
    address = {}
  ) => {
    try {
      publish(PUBSUB.GET_ALL_ADDRESS_API, {
        type: isFrom,
        isSubscriptionAddress: selectedAddressId,
        address: address,
      })
    } catch (error) {
      console.error('** SF - Error publishing:', error)
    }
  }

  publishTriggerPaymentApiEvent = (isFrom = '') => {
    try {
      publish(PUBSUB.GET_ALL_PAYMENT_API, { type: isFrom })
    } catch (error) {
      console.error('** SF - Error publishing:', error)
    }
  }

  componentWillUnmount() {
    unSubscribeEvent(this.shippingAddressModalToken)
    unSubscribeEvent(this.subscriptionPaymentModalToken)
    unSubscribeEvent(this.redirectToOverrideCartToken)
  }

  handleSavedAddressEditContinue = async address => {
    try {
      const selectedAddressId = this.selectedAddressDataToEdit?.addressId || 0
      const addressInfo = {
        addressId: selectedAddressId,
      }
      const isSubscriptionPinValidation = isFeatureEnabled(
        'validatePincodeInSubs'
      )
      let addressPatchResponse = await addressContainer.updateAddress(
        address,
        addressInfo,
        isSubscriptionPinValidation
      )
      const isSubscriptionAddress = selectedAddressId
        ? this.selectedAddressId == selectedAddressId
        : false

      if (addressContainer.isSuccessResponse(addressPatchResponse)) {
        // GPS payment options can depend on shipping destination, refresh after address save
        if (isSubscriptionAddress && isGPSEnabled()) {
          await checkoutContainer.getGlobalPaymentOptions(true, true)
        }
        toastState.setToastMessage(
          i18nTranslate(
            'address.updateAddressSuccess',
            'Your address has been updated.'
          ),
          true
        )
        const modifiedAddress = {
          ...address,
          profileAddressId: selectedAddressId,
          isDefault:
            address?.setDefaultAddress ||
            address?.setDefaultShippingAddress ||
            false,
          addressId: selectedAddressId,
        }
        this.publishTriggerAddressApiEvent(
          'Update',
          isSubscriptionAddress,
          modifiedAddress
        )
      }
    } catch (e) {
      toastState.setToastMessage(
        i18nTranslate(
          'address.updateAddressFailure',
          'Sorry, we are unable to update your address. Please try again.'
        ),
        false
      )
      console.log('** Error in publishing data')
    }
  }

  toggleSavedAddressEditModal = () => {
    this.isToShowSavedAddressEditModal = false
  }

  handleSubscriptionAddressEdit = address => {
    this.toggleSubscriptionAddressModal()
    this.isToShowSavedAddressEditModal = true
    this.selectedAddressDataToEdit = address || {}
  }

  handleSavedAddressContinue = selectedAddress => {
    const selectedSavedAddress = {
      ...selectedAddress,
      profileAddressId: selectedAddress?.addressId || 0,
    }
    this.publishSavedAddressEvent(selectedSavedAddress)
  }

  handleAddNewSubscriptionAddress = async (address = {}) => {
    const isSubscriptionPinValidation = isFeatureEnabled(
      'validatePincodeInSubs'
    )
    let addressPatchResponse = await addressContainer.addAddress(
      address,
      '',
      isSubscriptionPinValidation
    )
    if (addressContainer.isSuccessResponse(addressPatchResponse)) {
      // GPS payment options can depend on shipping destination, refresh after address save
      if (address?.isDefault && isGPSEnabled()) {
        await checkoutContainer.getGlobalPaymentOptions(true, true)
      }
      const addedAddress = {
        profileAddressId: addressPatchResponse?.addressId || '',
        addressId: addressPatchResponse?.addressId,
        ...address,
      }
      if (address?.isDefault) {
        this.publishAddAddressEvent(addedAddress)
      }
    } else {
      if (response?.code === 'SHSH0000012') {
        const errorMessage = i18nTranslate(
          'address.deliveryNotAvailableIndia',
          'Sorry, we do not deliver to your selected location. Please update your pin code and try again.'
        )

        // Track pincode error for GTM
        trackPincodeError({
          pincodeMsg: errorMessage,
        })

        toastState.setToastMessage(errorMessage)
      } else {
        toastState.setToastMessage(
          i18nTranslate(
            'address.addAddressFailure',
            'Unable to add address, please try again'
          )
        )
      }
    }
  }

  toggleSubscriptionAddressModal = () => {
    this.isToShowAddressModal = false
  }

  showShippingAddressModal = (options = '', data = {}) => {
    if (!this.isToShowAddressModal) {
      this.isToShowAddressModal = true
    }
    this.selectedAddressId = data?.publishData?.selectedAddressData || ''
  }

  showSubscriptionPaymentModal = async (options = '', data = {}) => {
    if (!this.isToShowPaymentModal) {
      this.isToShowPaymentModal = true
    }
    this.selectedPaymentId = data?.publishData?.selectedPaymentData || 0
    this.selectedAddressId = data?.publishData?.selectedAddressData || ''
  }

  // globalPaymentOptions is fetched here, on the 'Add a new payment method' tab
  // select, instead of whenever the payment modal opens (e.g. edit icon click).
  handleAddNewPaymentTabSelected = async () => {
    const subscriptionGlobalPaymentResponse = this.props?.globalPaymentResponse
    const hasGlobalPaymentResponse =
      subscriptionGlobalPaymentResponse &&
      Object.keys(subscriptionGlobalPaymentResponse).length > 0
    if (!isGPSEnabled() || hasGlobalPaymentResponse) {
      return
    }
    try {
      overlayState.showLoader()
      await this.props.getGlobalPaymentOptionsForSubscription?.()
    } finally {
      overlayState.hideLoader()
    }
  }

  toggleSubscriptionPaymentModal = () => {
    this.isToShowPaymentModal = false
  }

  handleSavedPaymentContinue = paymentData => {
    const paymentPayload = { paymentId: paymentData }
    this.publishSavedPaymentEvent(paymentPayload)
  }

  handleAddNewSubscriptionPayment = paymentData => {
    const paymentPayload = { paymentId: paymentData }
    this.publishAddNewPaymentEvent(paymentPayload)
  }

  handleEditSubscriptionPayment = () => {
    this.publishTriggerPaymentApiEvent('Update')
  }

  render() {
    // Shipping Address - Saved and Add New Tab Modal
    if (this.isToShowAddressModal) {
      return (
        <SubscriptionAddressModal
          data-testId="qa-subscription-modal"
          toggleAddressModal={this.toggleSubscriptionAddressModal}
          handleSavedAddressContinue={this.handleSavedAddressContinue}
          handleAddNewSubscriptionAddress={this.handleAddNewSubscriptionAddress}
          handleSubscriptionAddressEditModal={
            this.handleSubscriptionAddressEdit
          }
          isToShowAddressModal={this.isToShowAddressModal}
          firstTabName={i18nTranslate(
            'subscription.savedAddress',
            'Saved Address'
          )}
          secondTabName={i18nTranslate(
            'subscription.addNewAddress',
            'Add New Address'
          )}
          customModalTitle={i18nTranslate(
            'subscription.shippingTitle',
            'Shipping Address'
          )}
          selectedAddressId={this.selectedAddressId}
        />
      )
    }

    // Shipping Address - Saved Address Edit Modal
    if (this.isToShowSavedAddressEditModal) {
      return (
        <AddressEditModal
          data-testId="qa-subscription-modal"
          toggleSavedAddressEditModal={this.toggleSavedAddressEditModal}
          addressInfo={this.selectedAddressDataToEdit}
          handleEditSavedAddressContinue={this.handleSavedAddressEditContinue}
          isToShowSavedAddressEditModal={this.isToShowSavedAddressEditModal}
          customModalTitle={i18nTranslate(
            'subscription.EditExistingAddress',
            'Edit Address'
          )}
        />
      )
    }

    // Subscription Payment - Saved and Add New Tab Modal
    if (this.isToShowPaymentModal) {
      return (
        <SubscriptionPaymentModal
          data-testId="qa-subscription-modal"
          toggleSubscriptionPaymentModal={this.toggleSubscriptionPaymentModal}
          handleSavedPaymentContinue={this.handleSavedPaymentContinue}
          isToShowPaymentModal={this.isToShowPaymentModal}
          isGPS3DSEnabledInSubscription={
            this.props?.isGPS3DSEnabledInSubscription || false
          }
          triggerSubscription3DSInitiateCall={
            this.props?.triggerSubscription3DSInitiateCall
          }
          firstTabName={i18nTranslate(
            'checkout.savedPayment',
            'Saved Payments'
          )}
          secondTabName={i18nTranslate(
            'checkout.addPayment',
            'Add New Payment'
          )}
          customModalTitle={i18nTranslate(
            'subscription.paymentTitle',
            'Payment Method'
          )}
          selectedPaymentId={this.selectedPaymentId}
          selectedAddressId={this.selectedAddressId}
          handleEditSubscriptionPayment={this.handleEditSubscriptionPayment}
          handleAddNewSubscriptionPayment={this.handleAddNewSubscriptionPayment}
          onAddNewPaymentTabSelected={this.handleAddNewPaymentTabSelected}
          globalPaymentResponse={this.props.globalPaymentResponse}
        />
      )
    }

    return <></>
  }
}
export default SubscriptionModal
export { SubscriptionModal }
